import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, Search, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { BadgeStatus } from "@/components/ui/badge-status";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FieldGroup, FormGrid, Section } from "@/components/ui/section";
import {
  callFeasibility,
  fetchBuildingTypes,
  fetchMunicipalities,
} from "@/api/client";
import type { FeasibilitySummary } from "@/api/schemas";
import {
  FeasibilityResult,
  outcomeMeta,
  toFeasibilitySummary,
} from "@/features/feasibility/FeasibilityResult";
import { SpatialPlanWizard } from "@/features/feasibility/SpatialPlanWizard";
import {
  useProjectStore,
  type FeasibilityValues,
} from "@/features/projects/useProjectStore";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { countHr } from "@/lib/pluralHr";
import { cn } from "@/lib/utils";

const DEFAULT_VALUES: FeasibilityValues = {
  building_type: "obiteljska_kuca",
  building_subtype: "",
  context: "",
  designation: "",
  in_city_center: false,
  work_type: "novogradnja",
  municipality_code: "opatija",
};

/** Croatian display labels for the raw ``/api/building-types`` enum keys. */
const BUILDING_TYPE_LABELS: Record<string, string> = {
  obiteljska_kuca: "Obiteljska kuća",
  "individualna stambena": "Individualna stambena",
  "individualna stambena samostojeca": "Individualna stambena — samostojeća",
  "individualna stambena dvojna": "Individualna stambena — dvojna",
  visestambena: "Višestambena",
  pomocna: "Pomoćna",
  poslovna: "Poslovna",
  proizvodna: "Proizvodna",
  "ugostiteljsko turisticka": "Ugostiteljsko-turistička",
  drustvena: "Društvena",
  poljoprivredna: "Poljoprivredna",
  jednostavna: "Jednostavna",
};

export function buildingTypeLabel(t: string): string {
  const known = BUILDING_TYPE_LABELS[t];
  if (known) return known;
  const plain = t.replace(/_/g, " ");
  return plain.charAt(0).toUpperCase() + plain.slice(1);
}

/**
 * Provjera uvjeta gradnje as an in-page panel (formerly the standalone
 * Step 1). Advisory, never a gate: the architect who already knows the
 * answer leaves it collapsed and goes straight to "Generiraj
 * dokumentaciju"; the one who came for the verdict expands, runs the
 * check, and gets the same FeasibilityResult the old step rendered.
 * Form values autosave into the draft's ``feasibility`` slot — the
 * situacija/legal/completeness panels keep reading them from there.
 */
export function FeasibilityPanel({ projectId }: { projectId: string }) {
  const projects = useProjectStore((s) => s.projects);
  const upsert = useProjectStore((s) => s.upsert);
  const project = projects.find((p) => p.id === projectId);

  const [freshResult, setFreshResult] = useState<FeasibilitySummary | null>(null);
  // Collapsed by default — the header strip carries the verdict. Opens
  // automatically only on a fresh run so the result lands in view.
  const [open, setOpen] = useState(false);
  // Samostalni unos prostornog plana — wizard below the form (kept outside
  // the <form> element: it has its own inputs and must not nest forms).
  const [planWizardOpen, setPlanWizardOpen] = useState(false);

  const { register, handleSubmit, reset, watch, getValues, setValue } =
    useForm<FeasibilityValues>({
      defaultValues: project?.feasibility ?? DEFAULT_VALUES,
    });

  // Re-seed the form only when the route points at a different project —
  // NOT on every store write, or the autosave below would reset the form
  // (and the cursor) mid-typing.
  useEffect(() => {
    const current = useProjectStore
      .getState()
      .projects.find((p) => p.id === projectId);
    reset(current?.feasibility ?? DEFAULT_VALUES);
    setFreshResult(null);
    coerceBuildingType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reset]);

  // Draft autosave — losing a filled form to a refresh or a navigation
  // is the costliest UX failure on this form.
  const saveDraft = useDebouncedCallback((values: FeasibilityValues) => {
    const current = useProjectStore
      .getState()
      .projects.find((p) => p.id === projectId);
    if (!current) return;
    upsert({
      ...current,
      feasibility: values,
      updatedAt: new Date().toISOString(),
    });
  }, 800);

  useEffect(() => {
    const sub = watch((values) => saveDraft(values as FeasibilityValues));
    return () => sub.unsubscribe();
  }, [watch, saveDraft]);

  const muniQuery = useQuery({
    queryKey: ["municipalities"],
    queryFn: fetchMunicipalities,
  });
  const typesQuery = useQuery({
    queryKey: ["building-types"],
    queryFn: fetchBuildingTypes,
  });

  // A stored value outside the API list (e.g. the legacy
  // "obiteljska_kuca" default) makes the native select silently display
  // the first option while the form still holds the stale value —
  // coerce to a real option so what the user sees is what gets sent.
  const buildingTypes = typesQuery.data;
  function coerceBuildingType() {
    if (!buildingTypes?.length) return;
    if (!buildingTypes.includes(getValues("building_type"))) {
      setValue("building_type", buildingTypes[0], { shouldDirty: true });
    }
  }
  useEffect(() => {
    coerceBuildingType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingTypes]);

  const mutation = useMutation({
    mutationFn: (values: FeasibilityValues) => callFeasibility(values),
    onSuccess: (data, values) => {
      const summary = toFeasibilitySummary(data);
      setFreshResult(summary);
      const current = useProjectStore
        .getState()
        .projects.find((p) => p.id === projectId);
      if (current) {
        upsert({
          ...current,
          feasibility: values,
          feasibilityResult: summary,
          updatedAt: new Date().toISOString(),
        });
      }
      window.setTimeout(() => {
        document
          .querySelector('[data-testid="feasibility-result"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    },
  });

  if (!project) return null;

  const result = freshResult ?? project.feasibilityResult ?? null;
  const meta = result ? outcomeMeta(result.outcome_class) : null;
  const exceeded = result
    ? result.parameter_validation.filter((c) => c.status.toUpperCase() !== "OK").length
    : 0;

  return (
    <Card className="mb-6">
      <CardHeader>
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-ind-600" />
              Provjera uvjeta gradnje
            </span>
          </CardTitle>
          <span className="ml-2 flex flex-wrap items-center gap-2 text-[0.78rem] text-stone-500">
            {meta ? (
              <>
                <BadgeStatus kind={meta.kind}>{meta.label}</BadgeStatus>
                {result && result.conditions.length > 0 && (
                  <span>
                    {countHr(result.conditions.length, "uvjet", "uvjeta", "uvjeta")}
                  </span>
                )}
                {exceeded > 0 && (
                  <span className="text-red-700">
                    {countHr(exceeded, "prekoračenje", "prekoračenja", "prekoračenja")}
                  </span>
                )}
              </>
            ) : (
              <span className="text-stone-400">
                provjeri smije li se graditi — verdikt s citatom članka plana
              </span>
            )}
          </span>
          <ChevronDown
            className={cn(
              "ml-auto h-4 w-4 flex-shrink-0 text-stone-400 transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </CardHeader>

      {open && (
        <CardBody>
          <form
            onSubmit={(e) => {
              // Nested context safety: DataPage renders this panel near a
              // form-bearing page — stop propagation so submitting the
              // check can never bubble into another handler.
              e.stopPropagation();
              void handleSubmit((v) => mutation.mutate(v))(e);
            }}
          >
            <Section title="Tip projekta">
              <FormGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="fp_building_type" required>Tip građevine</Label>
                  <select
                    id="fp_building_type"
                    className="rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem] text-stone-800 focus-visible:border-ind-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ind-500/15"
                    {...register("building_type")}
                  >
                    {(typesQuery.data ?? []).map((t) => (
                      <option key={t} value={t}>
                        {buildingTypeLabel(t)}
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fp_building_subtype">Podtip</Label>
                  <Input id="fp_building_subtype" {...register("building_subtype")} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fp_work_type">Tip zahvata</Label>
                  <select
                    id="fp_work_type"
                    className="rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem]"
                    {...register("work_type")}
                  >
                    <option value="novogradnja">Novogradnja</option>
                    <option value="rekonstrukcija">Rekonstrukcija</option>
                    <option value="dogradnja">Dogradnja</option>
                  </select>
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="fp_municipality_code">Općina</Label>
                  <select
                    id="fp_municipality_code"
                    className="rounded-md border border-stone-300 bg-white px-3 py-[0.55rem] text-[0.88rem]"
                    {...register("municipality_code")}
                  >
                    {(muniQuery.data?.municipalities ?? []).map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="self-start text-[0.76rem] font-semibold text-ind-700 hover:underline"
                    onClick={() => setPlanWizardOpen((v) => !v)}
                  >
                    Tvoje općine nema? Učitaj prostorni plan →
                  </button>
                </FieldGroup>
              </FormGrid>
            </Section>

            <Section title="Lokacija i kontekst">
              <FormGrid cols={2}>
                <FieldGroup>
                  <Label htmlFor="fp_designation">Namjena (PPGO/PPUG)</Label>
                  <Input id="fp_designation" placeholder="npr. M1 / S1 / D1" {...register("designation")} />
                </FieldGroup>
                <FieldGroup>
                  <Label className="mt-7" htmlFor="fp_in_city_center">
                    <input
                      id="fp_in_city_center"
                      type="checkbox"
                      className="mr-2"
                      {...register("in_city_center")}
                    />
                    U gradskom središtu
                  </Label>
                </FieldGroup>
                <FieldGroup full>
                  <Label htmlFor="fp_context">Slobodan opis</Label>
                  <Textarea
                    id="fp_context"
                    rows={3}
                    placeholder="Npr. KIG 0.4, KISN 0.8, Po+P+1, visina v=6.5m, V=8.5m"
                    {...register("context")}
                  />
                </FieldGroup>
              </FormGrid>
            </Section>

            {mutation.error instanceof Error && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700">
                Greška: {mutation.error.message}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending}>
                <Search className="h-4 w-4" />
                {mutation.isPending
                  ? "Analiziram…"
                  : result
                    ? "Ponovi provjeru"
                    : "Provjeri mogućnosti gradnje"}
              </Button>
            </div>
          </form>

          {planWizardOpen && (
            <SpatialPlanWizard
              onClose={() => setPlanWizardOpen(false)}
              onActivated={(code) => {
                // The new municipality is live — point the form at it so the
                // next "Provjeri" runs against the just-approved rules.
                setValue("municipality_code", code, { shouldDirty: true });
              }}
            />
          )}

          {result && <FeasibilityResult summary={result} />}
        </CardBody>
      )}
    </Card>
  );
}
